import React, { useEffect, useState } from "react";
import { Card, Tag, Button, Input } from "antd";
import { PushpinFilled, LockOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { MemberProfile } from "../../../../model/members/MemberProfile";

interface NotesCardProps {
    member: MemberProfile;
    onSave: (notes: string) => void;
    saving?: boolean;
}

// Strip HTML so legacy notes (previously authored with the rich-text editor)
// render as readable plain text in the new TextArea rather than as raw markup.
const toPlainText = (value: string): string => {
    if (!value) return "";
    if (!/[<&]/.test(value)) return value;
    const el = document.createElement("div");
    el.innerHTML = value;
    return (el.textContent || el.innerText || "").trim();
};

// Staff notes — admin/staff only, prominent, always-editable inline. Carries an
// "Admin only" lock tag and a "Not visible to the member" caption so staff are
// never confused about visibility. Gated on mode.notes by the parent (never
// rendered in the member self-service view).
const NotesCard: React.FC<NotesCardProps> = ({ member, onSave, saving = false }) => {
    const initial = toPlainText(member.metadata?.Notes ?? "");
    const [draft, setDraft] = useState(initial);

    // Re-seed the draft when the note actually changes (different member, or a
    // server refresh) — not on every member object churn (e.g. a photo load),
    // which would otherwise discard an unsaved edit.
    useEffect(() => {
        setDraft(toPlainText(member.metadata?.Notes ?? ""));
    }, [member.membershipId, member.metadata?.Notes]);

    const dirty = draft !== initial;

    return (
        <Card
            size="small"
            styles={{
                header: { background: "#fff7e6", borderBottom: "1px solid #ffe7ba" },
                body: { padding: 14, background: "#fffdf7" },
            }}
            title={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <PushpinFilled style={{ color: "#d48806" }} />
                    <span>Staff notes</span>
                    <Tag bordered={false} color="gold" style={{ marginInlineStart: 2, marginInlineEnd: 0, fontWeight: 700 }}>
                        <LockOutlined style={{ fontSize: 11, marginInlineEnd: 4 }} />
                        Admin only
                    </Tag>
                </span>
            }
        >
            <Input.TextArea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder="Add a note visible only to staff…"
                style={{ background: "#fff", borderColor: "#ffe7ba", fontSize: 14, lineHeight: 1.55, color: "var(--ft-ink)" }}
            />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: 10,
                    flexWrap: "wrap",
                    gap: 8,
                }}
            >
                <span style={{ fontSize: 12, color: "var(--ft-muted)" }}>
                    <EyeInvisibleOutlined style={{ marginInlineEnd: 5 }} />
                    Not visible to the member
                </span>
                <Button size="small" type="primary" loading={saving} disabled={!dirty} onClick={() => onSave(draft)}>
                    Save note
                </Button>
            </div>
        </Card>
    );
};

export default NotesCard;
